/* eslint-disable @typescript-eslint/no-explicit-any */
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { getNumberLabel } from "../../../../../utils/numberUtils";
import { transformToTree, TreeItem } from "../../../../../utils/filters/utils";
import { CompanySpend } from "../../../../../legacy/queries/Companies";

export interface CategoryNode {
  id: string;
  name: string;
  value: number;
  percentage: number;
  color: string;
  children?: CategoryNode[];
  parentId?: string;
}

export interface HierarchicalSpendItem extends TreeItem {
  value: number;
  children?: HierarchicalSpendItem[];
}

// Define colors using MUI theme colors for better harmony
export function getColorForCategory(index: number): string {
  const colors = [
    // orange
    "#ffb74d",
    // blue
    "#81d4fa",
    // dark blue
    "#2196f3",
    // purple
    "#673ab7",
    // green
    "#66bb6a",
    // pink
    "#f06292",
    // light blue
    "#b2dfdb",
    // purple
    "#ab47bc",
    // yellow
    "#fdd835",
    // light green
    "#d4e157",
  ];
  return colors[index % colors.length];
}

/**
 * Transforms flat spend data into hierarchical structure
 * @param data Array of spend items from API
 * @returns Hierarchical tree structure with spend values
 */
export const transformFlatToHierarchicalSpend = (
  data: CompanySpend[]
): HierarchicalSpendItem[] => {
  // First transform to basic tree structure
  const treeData = transformToTree(
    data.map((item) => ({
      id: item.category.id,
      parentId: item.category.parentId,
      name: item.category.name,
      absoluteValue: item.absoluteValue,
    })),
    (item) => item.name,
    "0" // Root parent ID for spend data
  );

  // Create a map for quick value lookup
  const valueMap = new Map(
    data.map((item) => [item.category.id, item.absoluteValue])
  );

  // Convert to hierarchical structure with values
  const convertToHierarchicalSpend = (
    items: TreeItem[]
  ): HierarchicalSpendItem[] => {
    return items.map((item) => ({
      ...item,
      value: valueMap.get(item.id) || 0,
      children: item.children
        ? convertToHierarchicalSpend(item.children)
        : undefined,
    }));
  };

  return convertToHierarchicalSpend(treeData);
};

/**
 * Calculates progress percentage for a spend item
 * @param itemValue Value of the item
 * @param totalValue Total value for percentage calculation
 * @returns Progress percentage (0-100)
 */
/**
 * Converts HierarchicalSpendItem to CategoryNode with percentage calculations
 * @param items HierarchicalSpendItem array
 * @param totalValue Total value for percentage calculation
 * @returns CategoryNode array with percentages
 */
export const convertToProportionalData = (
  items: HierarchicalSpendItem[],
  totalValue?: number
): CategoryNode[] => {
  const levelTotal =
    totalValue || items.reduce((sum, item) => sum + item.value, 0);

  return items.map((item, index) => ({
    id: item.id,
    name: item.name,
    value: item.value,
    percentage: levelTotal > 0 ? (item.value / levelTotal) * 100 : 0,
    color: getColorForCategory(index),
    children: item.children
      ? convertToProportionalData(item.children, item.value)
      : undefined,
  }));
};

export const calculateProgress = (
  itemValue: number,
  totalValue: number
): number => {
  if (totalValue === 0) return 0;
  return Math.min((itemValue / totalValue) * 100, 100);
};

/**
 * Gets color for a specific hierarchy level
 * @param level The hierarchy level (0-based)
 * @returns Hex color string
 */
export const getColorByLevel = (level: number): string => {
  // Fixed colors for first two levels
  if (level === 0) return "#4caf50"; // Green for root level
  if (level === 1) return "#2196f3"; // Blue for first level

  // Extended color palette for deeper levels
  const extendedColors = [
    "#ffb74d", // orange
    "#81d4fa", // light blue
    "#673ab7", // purple
    "#66bb6a", // green
    "#f06292", // pink
    "#b2dfdb", // light blue
    "#ab47bc", // purple
    "#fdd835", // yellow
    "#d4e157", // light green
  ];

  // For levels 2+, cycle through the extended colors
  const colorIndex = (level - 2) % extendedColors.length;

  return extendedColors[colorIndex];
};

// Create and configure the AMCharts chart
export function createProportionalChart(
  chartElement: HTMLDivElement,
  currentData: CategoryNode[],
  handleDrillDown: (categoryName: string) => void
): am4charts.XYChart {
  // Create chart instance
  const chart = am4core.create(chartElement, am4charts.XYChart);

  // Prepare data - single row with all categories
  const chartData = [
    {
      category: "IT Spend",
      ...currentData.reduce((acc, item, index) => {
        acc[`value${index}`] = item.percentage;
        acc[`name${index}`] = item.name;
        acc[`absoluteValue${index}`] = item.value;
        acc[`hasChildren${index}`] = item.children && item.children.length > 0;
        return acc;
      }, {} as any),
    },
  ];

  chart.data = chartData;

  // Create axes
  const categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
  categoryAxis.dataFields.category = "category";
  categoryAxis.renderer.grid.template.disabled = true;
  categoryAxis.renderer.labels.template.disabled = true;
  categoryAxis.renderer.minGridDistance = 30;

  const valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
  valueAxis.renderer.grid.template.disabled = true;
  valueAxis.renderer.labels.template.disabled = true;
  valueAxis.min = 0;
  valueAxis.max = 100;
  valueAxis.strictMinMax = true;

  // Create series for each category
  currentData.forEach((category, index) => {
    const series = chart.series.push(new am4charts.ColumnSeries());
    series.dataFields.valueX = `value${index}`;
    series.dataFields.categoryY = "category";
    series.name = category.name;
    series.stacked = true;

    const mainColor = am4core.color(category.color);

    // Apply pattern to alternating bars (odd indexes)
    if (index % 2 === 1) {
      const pattern = new am4core.LinePattern();
      pattern.strokeWidth = 4;
      pattern.gap = 10.3;
      const size = 30;
      pattern.width = size + pattern.gap;
      pattern.height = size + pattern.gap;
      pattern.rotation = -45;
      pattern.stroke = mainColor.brighten(0.3);
      pattern.strokeOpacity = 0.7;
      pattern.backgroundFill = mainColor;
      pattern.backgroundOpacity = 1;

      series.fill = pattern;
    } else {
      series.fill = mainColor;
    }

    series.stroke = am4core.color("#fff");
    series.strokeWidth = 1;

    // Add label in the center of each segment
    const labelBullet = series.bullets.push(new am4charts.LabelBullet());
    labelBullet.label.text = `{name${index}}\n{value${index}.formatNumber('#.1')}%`;
    labelBullet.label.fill = am4core.color("#000");
    labelBullet.label.fontSize = 10;
    labelBullet.label.fontWeight = "700";
    labelBullet.label.textAlign = "middle";
    labelBullet.label.truncate = true;
    labelBullet.label.hideOversized = false;
    labelBullet.label.maxWidth = 10;
    labelBullet.locationX = 0.5;

    // Only show label if segment is wide enough
    labelBullet.label.adapter.add("text", function (text, target) {
      if (target.dataItem && target.dataItem.dataContext) {
        const context = target.dataItem.dataContext as any;
        const percentage = context[`value${index}`];
        if (percentage < 8) {
          // Hide label if segment is too small
          return "";
        }
        const categoryName = context[`name${index}`];
        const absoluteValue = context[`absoluteValue${index}`];

        const formattedValue = getNumberLabel(absoluteValue, {
          isCurrency: true,
          compact: true,
        });

        return `[bold]${categoryName} ${percentage.toFixed(1)}%\n${formattedValue}[/]`;
      }
      return text;
    });

    // Add tooltip with correct percentage and formatted value for ALL segments
    series.columns.template.adapter.add("tooltipText", function (text, target) {
      if (target.dataItem && target.dataItem.dataContext) {
        const context = target.dataItem.dataContext as any;
        const percentage = context[`value${index}`];
        const absoluteValue = context[`absoluteValue${index}`];
        const categoryName = context[`name${index}`];
        const formattedValue = getNumberLabel(absoluteValue, {
          isCurrency: true,
          compact: true,
        });
        return `${categoryName}: ${percentage.toFixed(1)}%\n${formattedValue}`;
      }
      return text;
    });

    // Enable tooltip for all columns
    series.columns.template.tooltipText = "{tooltipText}";

    // Add click interaction ONLY for categories with children
    if (category.children && category.children.length > 0) {
      // Add click to both series and columns template
      series.events.on("hit", () => {
        handleDrillDown(category.name);
      });

      series.columns.template.events.on("hit", () => {
        handleDrillDown(category.name);
      });

      // Add hover state for clickable items
      const hoverState = series.columns.template.states.create("hover");
      hoverState.properties.fillOpacity = 0.8;

      // Change cursor for clickable items
      series.columns.template.cursorOverStyle =
        am4core.MouseCursorStyle.pointer;
      series.cursorOverStyle = am4core.MouseCursorStyle.pointer;
    }
  });

  // Remove padding and make it compact
  chart.paddingTop = 10;
  chart.paddingBottom = 10;
  chart.paddingLeft = 10;
  chart.paddingRight = 10;

  // Make chart responsive
  chart.responsive.enabled = true;

  return chart;
}
